import z from "zod";
import { isAscendingOperator, isOrderedOperator } from "./types";

export const wizardSchema = z
	.object({
		// Step 1 — Info
		alarmType: z.enum(["device_health", "measurement"], {
			errorMap: () => ({ message: "Please select an alarm type" }),
		}),
		name: z
			.string({
				required_error: "Please enter an alarm name",
				invalid_type_error: "Please enter an alarm name",
			})
			.min(1, "Please enter an alarm name"),

		// Step 2 — Condition
		parameterId: z
			.number({
				required_error: "Please select a metric",
				invalid_type_error: "Please select a metric",
			})
			.transform(v => v as Dryp.ParameterId),
		operator: z.enum([">", "<", "=", ">=", "<="], {
			errorMap: () => ({ message: "Please select an operator" }),
		}),
		criticalThreshold: z.number({
			required_error: "Please enter a critical threshold",
			invalid_type_error: "Please enter a critical threshold",
		}),
		warningThreshold: z.number({ invalid_type_error: "Please enter a warning threshold" }).nullish(),
		durationMinutes: z.number().int().min(1, "Duration must be at least 1 minute").nullish(),
		datapointCount: z.number().int().min(1, "Data points must be at least 1").nullish(),

		criticalDeliveryMode: z.enum(["immediate", "daily_digest", "weekly_digest"]),
		warningDeliveryMode: z.enum(["immediate", "daily_digest", "weekly_digest"]).nullish(),

		// Step 3 — Scope
		structureScope: z.enum(["all", "include"], { errorMap: () => ({ message: "Please select scope" }) }),
		structureIds: z.array(z.string().transform(v => v as Dryp.StructureId)),

		// Step 4 — Recipients
		cooldownMinutes: z.number({ required_error: "Please select a cooldown" }),
		recipients: z
			.array(
				z
					.object({
						userId: z
							.string()
							.optional()
							.transform(v => v as Dryp.UserId | undefined),
						name: z.string().optional(),
						email: z.string().optional(),
					})
					.refine(r => r.userId || r.email, {
						message: "Recipient must have a userId (internal) or an email (external)",
					}),
			)
			.min(1, "Please select at least one recipient"),
	})
	.superRefine((val, ctx) => {
		if (val.structureScope === "include" && val.structureIds.length === 0) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				path: ["structureIds"],
				message: "Please select at least one structure",
			});
		}

		if ((val.durationMinutes ?? 0) > 0 && (val.datapointCount ?? 0) > 0) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				path: ["datapointCount"],
				message: "Choose either a sustained duration or a data-point count, not both.",
			});
		}

		if (val.warningThreshold != null) {
			if (!val.warningDeliveryMode) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					path: ["warningDeliveryMode"],
					message: "Choose how warning alerts are delivered.",
				});
			}

			if (!isOrderedOperator(val.operator)) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					path: ["warningThreshold"],
					message: "A warning level needs an ordered operator (>, ≥, <, ≤).",
				});
			} else {
				const ascending = isAscendingOperator(val.operator);
				const warningOnWrongSide = ascending
					? val.warningThreshold > val.criticalThreshold
					: val.warningThreshold < val.criticalThreshold;
				if (warningOnWrongSide) {
					ctx.addIssue({
						code: z.ZodIssueCode.custom,
						path: ["warningThreshold"],
						message: ascending
							? "Warning must be at or below the critical value — it should trigger first."
							: "Warning must be at or above the critical value — it should trigger first.",
					});
				}
			}
		}
	});
